// Modern invoice layout using @react-pdf/renderer
// Receives a single canonical InvoiceDisplayModel — no tax math inside this component.
import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import { fmtInr, numberToWords } from "./invoiceUtils";

const ModernLayout = ({ model }) => {
  const {
    invoice_number,
    invoice_date,
    due_date,
    notes,
    terms,
    items,
    subtotal,
    discountPercent,
    discountAmount,
    cgstPercent,
    cgstAmount,
    sgstPercent,
    sgstAmount,
    igstPercent,
    igstAmount,
    shipping,
    roundoff,
    total,
    organization,
    customer,
    customerShipped,
    template,
  } = model || {};

  const t = template || {};
  const primary = t.primary_color || "#002FA7";
  const secondary = t.secondary_color || "#0A0A0B";
  
  // Get item columns from template, default to modern columns
  const itemColumns = Array.isArray(t.item_columns) 
    ? t.item_columns 
    : ["item_name", "description", "hsn_sac", "quantity", "unit", "price", "amount"];

  // Use shipped customer if provided, otherwise bill-to customer
  const shipToCustomer = customerShipped || customer;

  const styles = StyleSheet.create({
    page: {
      padding: 50,
      fontFamily: "Helvetica",
      fontSize: 10,
    },
    header: {
      backgroundColor: primary,
      padding: 40,
      marginBottom: 32,
      color: "#fff",
    },
    headerContent: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
    headerCompany: {
      fontSize: 24,
      fontWeight: "bold",
      marginBottom: 4,
    },
    headerDescription: {
      fontSize: 12,
    },
    headerInvoice: {
      textAlign: "right",
    },
    headerInvoiceTitle: {
      fontSize: 28,
      fontWeight: "bold",
      marginBottom: 4,
    },
    headerInvoiceNumber: {
      fontSize: 14,
    },
    infoCards: {
      flexDirection: "row",
      marginBottom: 32,
    },
    infoCard: {
      flex: 1,
      backgroundColor: "#f8f9fa",
      padding: 20,
      marginHorizontal: 5,
    },
    infoCardLabel: {
      fontSize: 10,
      color: "#666",
      marginBottom: 8,
      fontWeight: "bold",
    },
    infoCardValue: {
      fontSize: 16,
      fontWeight: "bold",
      color: primary,
    },
    clientInfo: {
      marginBottom: 32,
    },
    clientInfoTitle: {
      fontSize: 12,
      fontWeight: "bold",
      color: secondary,
      marginBottom: 16,
      letterSpacing: 1,
    },
    clientGrid: {
      flexDirection: "row",
    },
    clientSection: {
      flex: 1,
      marginHorizontal: 10,
    },
    clientLabel: {
      fontSize: 10,
      color: "#666",
      marginBottom: 4,
    },
    clientName: {
      fontSize: 14,
      fontWeight: "bold",
      color: "#000",
    },
    clientDetails: {
      fontSize: 12,
      color: "#333",
    },
    table: {
      marginBottom: 32,
    },
    tableTitle: {
      fontSize: 12,
      fontWeight: "bold",
      color: secondary,
      marginBottom: 16,
      letterSpacing: 1,
    },
    tableHeader: {
      flexDirection: "row",
      backgroundColor: primary,
      color: "#fff",
    },
    tableRow: {
      flexDirection: "row",
      borderBottomWidth: 1,
      borderBottomColor: "#eee",
    },
    tableCell: {
      padding: 16,
      fontSize: 10,
    },
    itemName: {
      fontWeight: "bold",
    },
    hsn: {
      fontSize: 8,
      color: "#999",
    },
    textCenter: {
      textAlign: "center",
    },
    textRight: {
      textAlign: "right",
    },
    summary: {
      backgroundColor: "#f8f9fa",
      padding: 32,
      marginBottom: 32,
    },
    summaryTitle: {
      fontSize: 12,
      fontWeight: "bold",
      color: secondary,
      marginBottom: 16,
      letterSpacing: 1,
    },
    summaryGrid: {
      flexDirection: "row",
    },
    summaryLeft: {
      flex: 1,
    },
    summaryRight: {
      flex: 1,
      textAlign: "right",
    },
    summaryLabel: {
      fontSize: 10,
      color: "#666",
      marginBottom: 8,
    },
    summaryValue: {
      fontSize: 14,
      fontWeight: "bold",
    },
    totalAmount: {
      fontSize: 28,
      fontWeight: "bold",
      color: primary,
    },
    amountWords: {
      fontSize: 10,
      color: "#666",
      fontStyle: "italic",
      marginTop: 8,
    },
    footer: {
      textAlign: "center",
      fontSize: 10,
      color: "#999",
      marginTop: 48,
    },
  });

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <View>
              <Text style={styles.headerCompany}>
                {organization?.organization_name || "Your Business"}
              </Text>
              <Text style={styles.headerDescription}>
                {organization?.description || "Professional Services"}
              </Text>
            </View>
            <View style={styles.headerInvoice}>
              <Text style={styles.headerInvoiceTitle}>
                {t.invoice_title || "INVOICE"}
              </Text>
              <Text style={styles.headerInvoiceNumber}>
                #{invoice_number}
              </Text>
            </View>
          </View>
        </View>

        {/* Invoice Info Cards */}
        <View style={styles.infoCards}>
          <View style={styles.infoCard}>
            <Text style={styles.infoCardLabel}>INVOICE DATE</Text>
            <Text style={styles.infoCardValue}>{invoice_date || "-"}</Text>
          </View>
          <View style={styles.infoCard}>
            <Text style={styles.infoCardLabel}>DUE DATE</Text>
            <Text style={styles.infoCardValue}>{due_date || "-"}</Text>
          </View>
          <View style={styles.infoCard}>
            <Text style={styles.infoCardLabel}>TOTAL AMOUNT</Text>
            <Text style={styles.infoCardValue}>{fmtInr(total)}</Text>
          </View>
        </View>

        {/* Client Info */}
        <View style={styles.clientInfo}>
          <Text style={styles.clientInfoTitle}>CLIENT INFORMATION</Text>
          <View style={styles.clientGrid}>
            <View style={styles.clientSection}>
              <Text style={styles.clientLabel}>BILL TO</Text>
              <Text style={styles.clientName}>{customer?.name || "-"}</Text>
              <Text style={styles.clientDetails}>
                {customer?.address || "-"}
              </Text>
              <Text style={styles.clientDetails}>
                {customer?.email || "-"}
              </Text>
            </View>
            {shipToCustomer && shipToCustomer !== customer && (
              <View style={styles.clientSection}>
                <Text style={styles.clientLabel}>SHIP TO</Text>
                <Text style={styles.clientName}>
                  {shipToCustomer.name || "-"}
                </Text>
                <Text style={styles.clientDetails}>
                  {shipToCustomer.address || "-"}
                </Text>
                <Text style={styles.clientDetails}>
                  {shipToCustomer.email || "-"}
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* Items Table */}
        <View style={styles.table}>
          <Text style={styles.tableTitle}>ITEMS</Text>
          <View style={styles.tableHeader}>
            <Text
              style={[styles.tableCell, styles.textCenter, { flex: 0.5 }]}
            >
              Sr No.
            </Text>
            {itemColumns.map((col) => {
              const columnConfig = {
                item_name: { label: "DESCRIPTION", flex: 3, align: "left" },
                description: { label: "DESCRIPTION", flex: 2, align: "left" },
                hsn_sac: { label: "HSN/SAC", flex: 1, align: "center" },
                quantity: { label: "QTY", flex: 0.5, align: "center" },
                unit: { label: "UNIT", flex: 0.5, align: "center" },
                price: { label: "RATE", flex: 1, align: "right" },
                amount: { label: "AMOUNT", flex: 1, align: "right" },
              };
              const config = columnConfig[col] || { label: col, flex: 1, align: "left" };
              return (
                <Text
                  key={col}
                  style={[
                    styles.tableCell,
                    config.align === "center" && styles.textCenter,
                    config.align === "right" && styles.textRight,
                    { flex: config.flex }
                  ]}
                >
                  {config.label}
                </Text>
              );
            })}
          </View>
          {(items || []).map((item, idx) => (
            <View key={item.id || idx} style={styles.tableRow}>
              <Text
                style={[
                  styles.tableCell,
                  styles.textCenter,
                  { flex: 0.5 },
                ]}
              >
                {idx + 1}
              </Text>
              {itemColumns.map((col) => {
                const columnConfig = {
                  item_name: { flex: 3, align: "left" },
                  description: { flex: 2, align: "left" },
                  hsn_sac: { flex: 1, align: "center" },
                  quantity: { flex: 0.5, align: "center" },
                  unit: { flex: 0.5, align: "center" },
                  price: { flex: 1, align: "right" },
                  amount: { flex: 1, align: "right" },
                };
                const config = columnConfig[col] || { flex: 1, align: "left" };
                
                // Special handling for item_name column to show HSN below it
                if (col === "item_name") {
                  return (
                    <View key={col} style={[styles.tableCell, { flex: config.flex }]}>
                      <Text style={styles.itemName}>{item.item_name}</Text>
                      {item.hsn_sac && !itemColumns.includes("hsn_sac") && (
                        <Text style={styles.hsn}>HSN: {item.hsn_sac}</Text>
                      )}
                    </View>
                  );
                }
                
                const value = col === "price" ? fmtInr(item.price) 
                  : col === "amount" ? fmtInr(item.amount)
                  : item[col] || "-";
                return (
                  <Text
                    key={col}
                    style={[
                      styles.tableCell,
                      config.align === "center" && styles.textCenter,
                      config.align === "right" && styles.textRight,
                      { flex: config.flex }
                    ]}
                  >
                    {value}
                  </Text>
                );
              })}
            </View>
          ))}
        </View>

        {/* Summary */}
        <View style={styles.summary}>
          <Text style={styles.summaryTitle}>PAYMENT SUMMARY</Text>
          <View style={styles.summaryGrid}>
            <View style={styles.summaryLeft}>
              <Text style={styles.summaryLabel}>Subtotal</Text>
              <Text style={styles.summaryValue}>{fmtInr(subtotal)}</Text>
              {discountAmount > 0 && (
                <>
                  <Text style={styles.summaryLabel}>
                    Discount ({discountPercent}%)
                  </Text>
                  <Text style={styles.summaryValue}>
                    -{fmtInr(discountAmount)}
                  </Text>
                </>
              )}
              {cgstAmount > 0 && (
                <>
                  <Text style={styles.summaryLabel}>
                    CGST ({cgstPercent}%)
                  </Text>
                  <Text style={styles.summaryValue}>
                    {fmtInr(cgstAmount)}
                  </Text>
                </>
              )}
              {sgstAmount > 0 && (
                <>
                  <Text style={styles.summaryLabel}>
                    SGST ({sgstPercent}%)
                  </Text>
                  <Text style={styles.summaryValue}>
                    {fmtInr(sgstAmount)}
                  </Text>
                </>
              )}
              {igstAmount > 0 && (
                <>
                  <Text style={styles.summaryLabel}>
                    IGST ({igstPercent}%)
                  </Text>
                  <Text style={styles.summaryValue}>
                    {fmtInr(igstAmount)}
                  </Text>
                </>
              )}
              {shipping > 0 && (
                <>
                  <Text style={styles.summaryLabel}>Shipping</Text>
                  <Text style={styles.summaryValue}>
                    {fmtInr(shipping)}
                  </Text>
                </>
              )}
              {roundoff !== 0 && Math.abs(roundoff) > 0.01 && (
                <>
                  <Text style={styles.summaryLabel}>Roundoff</Text>
                  <Text style={styles.summaryValue}>
                    {roundoff > 0 ? "+" : ""}
                    {fmtInr(roundoff)}
                  </Text>
                </>
              )}
            </View>
            <View style={styles.summaryRight}>
              <Text style={styles.summaryLabel}>Total</Text>
              <Text style={styles.totalAmount}>{fmtInr(total)}</Text>
              <Text style={styles.amountWords}>
                {numberToWords(total || 0)}
              </Text>
            </View>
          </View>
        </View>

        {/* Terms */}
        {t.show_terms !== false && terms && (
          <View style={{ marginBottom: 16 }}>
            <Text
              style={{ fontWeight: "bold", fontSize: 12, marginBottom: 8 }}
            >
              Terms & Conditions
            </Text>
            <Text style={{ fontSize: 9, lineHeight: 1.5 }}>{terms}</Text>
          </View>
        )}

        {/* Notes */}
        {t.show_notes !== false && notes && (
          <View style={{ marginBottom: 16 }}>
            <Text
              style={{ fontWeight: "bold", fontSize: 12, marginBottom: 8 }}
            >
              Notes
            </Text>
            <Text style={{ fontSize: 9, lineHeight: 1.5 }}>{notes}</Text>
          </View>
        )}

        {/* Footer */}
        <View style={styles.footer}>
          {t.footer_text && <Text>{t.footer_text}</Text>}
          {t.show_powered_by !== false && <Text>Powered by BillForge</Text>}
        </View>
      </Page>
    </Document>
  );
};

ModernLayout.displayName = "ModernLayout";

export default ModernLayout;
